import { Link } from "@tanstack/react-router";
import {
  Award,
  BarChart3,
  BookOpen,
  ClipboardList,
  FileText,
  GraduationCap,
  LayoutDashboard,
  LifeBuoy,
  LineChart,
  LogOut,
  Settings,
  Shield,
  type LucideIcon,
} from "lucide-react";

import type { CurrentUser } from "@/api/client";
import { FEATURE_NAV_LABEL } from "@/lib/asset-labels";
import { Logo } from "@/components/Logo";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  deriveConceptualRole,
  FULL_LABEL,
  NAV_LINK_LABEL,
  type ConceptualRole,
} from "@/lib/role-labels";
import { cn } from "@/lib/utils";

type Role = NonNullable<CurrentUser["role"]>;

interface NavItem {
  /** Route target. Omitted for non-routing placeholders. */
  to?: string;
  label: string;
  icon: LucideIcon;
  /**
   * Match the active state only when the URL is exactly `to`. Default
   * false (prefix match), which is correct for items whose route has
   * children NOT separately listed in the sidebar (Settings, Admin
   * Dashboard). Set true for section-root Dashboards whose children
   * ARE separately listed (Partner / Manager) — otherwise the prefix
   * match keeps Dashboard highlighted while a sibling sub-item should
   * own the active state.
   */
  exact?: boolean;
  /**
   * Non-routing "coming soon" item — surfaces a label in the RA/RM nav
   * for a destination that isn't built yet (RACCPE-17). Renders as an
   * inert button, not a Link.
   */
  placeholder?: boolean;
  /**
   * HF-04: opens the Help & Feedback drawer instead of navigating. Renders
   * as a functional button (not inert) wired to the Layout's onOpenFeedback.
   */
  helpFeedback?: boolean;
}

interface NavSection {
  /** Heading shown above the items; null = no heading (general section). */
  label: string | null;
  /** Roles allowed to see this section. */
  visibleTo: Role[];
  items: NavItem[];
}

// Cross-role nav vocabulary (RACCPE-17): "Assets" → "Knowledge Base" (via
// FEATURE_NAV_LABEL; renamed from "Sales Toolkit" per CHP-423), per-section
// "My deals"/"Deal queue" → "Deals", and
// "SPIFs"/"SPIF queue" → "Rewards".
//
// RA/RM get a single flat nav (no PO/CM section headings) matching the
// ticket's Reset Admin / Reset Master list, rather than the overlapping
// PO+CM+admin sections a viewer with the `admin` role would otherwise see
// (which produced duplicate "Deals"/"Rewards" entries). The Rewards nav entry
// (CHP-430) points at the /dashboard/manager/spifs "coming soon" placeholder —
// Rewards is NOT cancelled, just parked (ADR-0033 supersedes ADR-0031 §4) — and
// Program Insights is a real link (/dashboard/manager/insights).
// Help and Feedback opens the feedback drawer (built). Deals
// (→/dashboard/manager/deals, the shared CM/RA/RM review surface — RACCPE-20),
// Knowledge Base, Training, Admin Hub (RM-only, →/dashboard/admin) and Settings
// are real links.
// CHP-520 removed the standalone "Dashboard" nav item (the Operations Core Main
// Dashboard is gone): the redirector now lands CM and RA on the Deals queue and
// RM on Program Insights. The viewer's RA-vs-RM identity is surfaced in the
// footer role label, so the flat section needs no heading.
//
// po-dashboard: a PO (channel_partner AND is_org_admin === true) sees an
// "Insights & Admin" entry pinned to the TOP of the partner section. BDMs
// (is_org_admin === false/null) do not. The org-name prefix was dropped from
// this nav label per the Insights redesign (the menu-bar copy ruling).
//
// CHP-251 / CHP-1049: the "Training" entry in the RA/RM nav is capability-aware.
// A user who can author (RM bypass, or RA holding Perm_Training_Upload/Edit —
// `canAuthorTraining`) points at the module-AUTHORING surface
// (/dashboard/admin/training, backend-gated `require_training_author`). A
// perm-less RA instead gets a READ-ONLY entry into the shared hub
// (/dashboard/partner/training): CHP-1049 restores a Training module for that
// population so they can QA published content + the quiz answer key (via the
// `require_admin`-gated preview), with no authoring rights. The CM "Training"
// entry below is the partner-pool view-only surface, tied to the manager role
// rather than a content permission, so it is NOT gated here; the "Knowledge
// Base" entry is the consumer browse surface and likewise stays.
function buildSections(
  conceptual: ConceptualRole | null,
  isOrgAdmin: boolean | null,
  canAuthorTraining: boolean,
): NavSection[] {
  if (conceptual === "ra" || conceptual === "rm") {
    return [
      {
        label: null,
        visibleTo: ["admin"],
        items: [
          // CHP-520: the standalone "Dashboard" item is gone (Operations Core
          // Main Dashboard removed); Program Insights leads the staff nav.
          // Program Insights (Wave 2): now a real link to the program-wide
          // Insights surface (was an inert "coming soon" placeholder). Product
          // nav copy from RACCPE-17, not a role-vocabulary token.
          {
            to: "/dashboard/manager/insights",
            // eslint-disable-next-line no-restricted-syntax
            label: "Channel Partner Program Insights",
            icon: LineChart,
            exact: true,
          },
          { to: "/dashboard/manager/deals", label: "Deals", icon: FileText },
          { to: "/dashboard/assets", label: FEATURE_NAV_LABEL, icon: BookOpen },
          // Rewards (CHP-430): the Rewards nav entry points at the
          // /dashboard/manager/spifs "coming soon" placeholder. Rewards is NOT
          // cancelled — it is parked behind the placeholder until the programme
          // surface is built (ADR-0033, supersedes ADR-0031 §4). Final "coming
          // soon" affordance pending the A3 design ruling.
          { to: "/dashboard/manager/spifs", label: "Rewards", icon: Award },
          // CHP-251 / CHP-1049: a user who can author training (RM bypass, or RA
          // holding Perm_Training_Upload/Edit) gets the AUTHORING entry
          // (/dashboard/admin/training, backend-gated `require_training_author`).
          // A perm-less RA instead gets a READ-ONLY entry into the shared hub
          // (/dashboard/partner/training) — they QA published content + the quiz
          // answer key via the `require_admin`-gated preview, with no authoring
          // rights (CHP-1049). A perm-less RM never reaches the else branch
          // (master bypass keeps canAuthorTraining true), so it is RA-only.
          ...(canAuthorTraining
            ? [
                {
                  to: "/dashboard/admin/training",
                  label: "Training",
                  icon: GraduationCap,
                } satisfies NavItem,
              ]
            : [
                {
                  to: "/dashboard/partner/training",
                  label: "Training",
                  icon: GraduationCap,
                } satisfies NavItem,
              ]),
          // Admin Hub = the user-management dashboard, RM-only per RACCPE-17.
          ...(conceptual === "rm"
            ? [
                {
                  to: "/dashboard/admin",
                  label: "Admin Hub",
                  icon: Shield,
                  exact: true,
                } satisfies NavItem,
              ]
            : []),
          { label: "Help and Feedback", icon: LifeBuoy, helpFeedback: true },
          { to: "/settings", label: "Settings", icon: Settings },
        ],
      },
    ];
  }
  return [
    {
      label: NAV_LINK_LABEL.po,
      visibleTo: ["channel_partner"],
      items: [
        { to: "/dashboard/partner", label: "Dashboard", icon: LayoutDashboard, exact: true },
        // po-dashboard: PO-only Insights & Admin. CHP-823: it sits SECOND, just
        // after Dashboard (was pinned above it). is_org_admin === true is the
        // strict PO test (null/false → BDM, no entry).
        ...(isOrgAdmin === true
          ? [
              {
                to: "/dashboard/partner/insights",
                // Product nav copy. The org-name prefix was dropped per the
                // Insights redesign menu-bar ruling.
                label: "Insights & Admin",
                icon: BarChart3,
                exact: true,
              } satisfies NavItem,
            ]
          : []),
        { to: "/dashboard/partner/deals", label: "Deals", icon: FileText },
        // CHP-271: Knowledge Base sits directly below Deals for BDM/PO (moved
        // out of the shared section below, which is now Help/Settings only).
        { to: "/dashboard/assets", label: FEATURE_NAV_LABEL, icon: BookOpen },
        // Rewards (CHP-430): points at the /dashboard/partner/spifs "coming
        // soon" placeholder. Rewards is NOT cancelled — it is parked behind the
        // placeholder until the programme surface is built (ADR-0033, supersedes
        // ADR-0031 §4). Final "coming soon" affordance pending the A3 ruling.
        { to: "/dashboard/partner/spifs", label: "Rewards", icon: Award },
        { to: "/dashboard/partner/training", label: "Training", icon: GraduationCap },
      ],
    },
    {
      // CM nav: the exact list + order from RACCPE-18 (CHP-218), plus the
      // Admin Hub added above Help and Feedback by CHP-288.
      // Knowledge Base + Settings are pulled into this CM-specific section
      // (rather than the shared one below) so they land in the ticket's
      // prescribed order; the shared section is therefore PO/BDM-only. The CM
      // Rewards link (CHP-430) points at the /dashboard/manager/spifs "coming
      // soon" placeholder (Rewards is NOT cancelled — parked behind the
      // placeholder; ADR-0033 supersedes ADR-0031 §4). Insights + Training are
      // real links; Help and Feedback opens the feedback drawer.
      label: NAV_LINK_LABEL.cm,
      visibleTo: ["channel_manager"],
      items: [
        // CHP-520: the standalone "Dashboard" item is gone (Operations Core Main
        // Dashboard removed); the CM nav now leads with Program Insights.
        // Program Insights (Wave 2): now a real link to the program-wide
        // Insights surface (was an inert "coming soon" placeholder). Product nav
        // copy from RACCPE-18, not a role-vocabulary token.
        {
          to: "/dashboard/manager/insights",
          // eslint-disable-next-line no-restricted-syntax
          label: "Channel Partner Program Insights",
          icon: LineChart,
          exact: true,
        },
        { to: "/dashboard/manager/deals", label: "Deals", icon: ClipboardList },
        { to: "/dashboard/assets", label: FEATURE_NAV_LABEL, icon: BookOpen },
        // Rewards (CHP-430): points at the /dashboard/manager/spifs "coming
        // soon" placeholder (ADR-0033, supersedes ADR-0031 §4).
        { to: "/dashboard/manager/spifs", label: "Rewards", icon: Award },
        // CHP-1013: the CM "Training" entry is back — VIEW-ONLY per the
        // designer ruling. It points at the shared training hub, which
        // suppresses all progress/quiz affordances for a channel_manager (its
        // predecessor deep-linked the CM Admin Hub's "Training Status" tab,
        // dead-linked after CHP-852 removed that tab, and was interim-removed).
        { to: "/dashboard/partner/training", label: "Training", icon: GraduationCap },
        // CHP-288: CM Admin Hub sits directly above Help and Feedback.
        { to: "/dashboard/manager/admin", label: "Admin Hub", icon: Shield, exact: true },
        { label: "Help and Feedback", icon: LifeBuoy, helpFeedback: true },
        { to: "/settings", label: "Settings", icon: Settings },
      ],
    },
    {
      // PO/BDM-only shared tail. Knowledge Base moved up under Deals (CHP-271),
      // so this section is now Help and Feedback + Settings.
      label: null,
      visibleTo: ["channel_partner"],
      items: [
        { label: "Help and Feedback", icon: LifeBuoy, helpFeedback: true },
        { to: "/settings", label: "Settings", icon: Settings },
      ],
    },
  ];
}

interface SidebarProps {
  data: CurrentUser;
  onSignOut: () => void;
  /** Fires on any nav-link click. Used by the drawer to auto-close on navigation. */
  onNavigate?: () => void;
  /** HF-04: opens the Help & Feedback drawer from the sidebar item. */
  onOpenFeedback?: () => void;
}

export function Sidebar({ data, onSignOut, onNavigate, onOpenFeedback }: SidebarProps) {
  const conceptual = deriveConceptualRole({
    role: data.role,
    is_org_admin: data.is_org_admin,
    is_resetdata_master: data.is_resetdata_master,
  });
  const roleLabel = conceptual ? FULL_LABEL[conceptual] : null;
  const displayName = data.full_name?.trim() || data.email?.split("@")[0] || data.email || "";
  const initials = computeInitials(data.full_name ?? null, data.email ?? "");
  // CHP-251: resolve the training-authoring capability the same way the backend
  // does — RM bypasses; otherwise the user must hold Perm_Training_Upload OR
  // Perm_Training_Edit (the keys behind `require_training_author`). The map is
  // already master-bypassed server-side; `conceptual === "rm"` is the belt-and-
  // suspenders for fixtures that omit the (optional) map.
  const perms = data.effective_permissions ?? {};
  const canAuthorTraining =
    conceptual === "rm" || perms.Perm_Training_Upload === true || perms.Perm_Training_Edit === true;
  const sections = buildSections(conceptual, data.is_org_admin ?? null, canAuthorTraining);

  return (
    <div className="bg-card flex h-full flex-col gap-1 border-r border-border p-4">
      <Link
        to="/dashboard"
        className="flex items-center px-3 pb-4 pt-2"
        aria-label="ResetData PRM — go to dashboard"
        onClick={onNavigate}
      >
        <Logo className="h-5" />
      </Link>

      <nav aria-label="primary" className="flex flex-1 flex-col gap-1 overflow-y-auto">
        {sections.map((section, idx) => {
          if (!data.role || !section.visibleTo.includes(data.role)) return null;
          return (
            <div key={idx} className="flex flex-col space-y-1">
              {section.label ? (
                <p className="px-3 pb-1 pt-4 font-mono text-2xs uppercase tracking-[0.12em] text-muted-foreground">
                  {section.label}
                </p>
              ) : null}
              {section.items.map((item) => {
                const Icon = item.icon;
                // HF-04: functional button that opens the Help & Feedback drawer
                // (checked before the placeholder branch — it has no `to`).
                if (item.helpFeedback) {
                  return (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => {
                        onNavigate?.();
                        onOpenFeedback?.();
                      }}
                      className={cn(
                        "flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-muted-foreground transition-colors",
                        "hover:bg-sidebar-accent hover:text-foreground",
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" aria-hidden />
                      <span className="flex-1">{item.label}</span>
                    </button>
                  );
                }
                if (item.placeholder || !item.to) {
                  return (
                    <button
                      key={item.label}
                      type="button"
                      aria-disabled="true"
                      title="Coming soon"
                      className={cn(
                        "flex cursor-default items-center gap-3 rounded-lg px-3 py-2 text-left text-sm text-muted-foreground/50",
                      )}
                      onClick={(e) => e.preventDefault()}
                    >
                      <Icon className="h-4 w-4 shrink-0" aria-hidden />
                      <span className="flex-1">{item.label}</span>
                    </button>
                  );
                }
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={onNavigate}
                    activeProps={{ className: "active" }}
                    activeOptions={{ exact: item.exact ?? false }}
                    className={cn(
                      // Canonical design-system nav item (Styleguide "Navigation"):
                      // rounded-lg, gap-3, px-3 py-2, text-sm.
                      "flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors",
                      // Inactive hover: a visibly darker light-grey fill
                      // (`sidebar-accent`, 88% per the Light-Mode Design Spec)
                      // + foreground text — the old `muted` (96%) wash read as
                      // near-invisible (CHP-786).
                      "hover:bg-sidebar-accent hover:text-foreground",
                      // Active: brand-tint (coral) wash + coral text + medium
                      // weight, matching the styleguide active item.
                      "[&.active]:bg-brand-tint [&.active]:font-medium [&.active]:text-primary",
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" aria-hidden />
                    <span className="flex-1">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="mt-auto flex flex-col gap-3 border-t border-border pt-3">
        {/* The profile chip (initials + name) is a second route into Settings,
            per the spec's "Accessing Settings" AC ("Profile picture/initials
            also link to this page."). The whole chip is the link; the avatar's
            visual initials stay aria-hidden so the link's accessible name is the
            visible display name, not a duplicated initials string (CHP-809
            finding 9). */}
        <Link
          to="/settings"
          onClick={onNavigate}
          aria-label={`${displayName} — open Settings`}
          className="flex items-center gap-3 rounded-lg px-1 py-1 transition-colors hover:bg-muted [&.active]:bg-brand-tint"
          activeProps={{ className: "active" }}
        >
          {/* CHP-815: render the uploaded avatar (falling back to initials) so a
              photo set in Settings propagates to the sidebar chip. The whole
              avatar stays aria-hidden — the link's accessible name is the
              display name (CHP-809 finding 9), not a duplicated initials/photo. */}
          <Avatar aria-hidden className="h-8 w-8 flex-shrink-0">
            {data.avatar_url ? <AvatarImage src={data.avatar_url} alt="" /> : null}
            <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="break-words text-[13px] font-medium leading-tight text-foreground">
              {displayName}
            </p>
            {roleLabel ? (
              <p className="text-[11.5px] leading-tight text-muted-foreground">{roleLabel}</p>
            ) : null}
          </div>
        </Link>
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-start gap-2"
          onClick={onSignOut}
        >
          <LogOut className="h-4 w-4" aria-hidden />
          Sign out
        </Button>
      </div>
    </div>
  );
}

function computeInitials(fullName: string | null, email: string): string {
  if (fullName && fullName.trim()) {
    const parts = fullName.trim().split(/\s+/);
    const first = parts[0]?.[0] ?? "";
    const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
    return (first + last).toUpperCase() || email[0]!.toUpperCase();
  }
  return (email[0] ?? "?").toUpperCase();
}
